import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import MuiTable from "@mui/material/Table";
import AddIcon from "@mui/icons-material/Add";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  designColors,
  designRadii,
  HEADER_ACTION_BUTTON_WIDTH,
} from "services/constants/designTokens";
import {
  sharedListCardSx,
  sharedListPageSurfaceSx,
  sharedPanelTextUi,
  sharedStickyCustomFilterBarSx,
  sharedStickyFooterBarSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";

// Mirrors Home/homeUi.js's role for this screen — a shared place for the
// Approval Center to pull its design tokens from, so the sub-screens
// (Leave, Overtime, Off-Day Duty, Work Adjustment, Advance Salary, Expense)
// have the same tokens available as they're brought onto the shared design
// system one at a time.
export const approvePageSurfaceSx = {
  ...sharedListPageSurfaceSx,
};

export const approveCardSx = {
  ...sharedListCardSx,
};

// The in-page category nav (Approval Center, Leave, Extra Work, ...) only
// needs to fit short icon+label rows plus one longer nested child label
// ("Attendance Correction") — the app's shared `sideBarWidth` (280,
// services/constants/index.js) was sized for other screens' wider content
// and left this nav looking oversized next to it. Nudged back up slightly
// from the first pass (232) since that read a bit tight.
export const approveSidebarWidth = 272;

// Each approval category (Leave, Overtime, ...) keeps its own accent color
// for quick visual scanning — this only standardizes the neutral chrome
// (radius, border, hover) around that accent, not the accent itself.
export const approveSidebarItemSx = ({ active, accent }) => ({
  px: 2.25,
  py: 1.2,
  borderRadius: designRadii.control,
  borderLeft: active ? `3px solid ${accent}` : "3px solid transparent",
  backgroundColor: active ? `${accent}14` : "transparent",
  "&:hover": { backgroundColor: active ? `${accent}14` : designColors.slate100 },
});

// Sticky list-view chrome, ported byte-for-byte from Administration's Users
// list (adminListScreenUi.js): a CustomFilter bar pinned under the fixed
// AppBar, a table head pinned under that (offset by the filter bar's own
// measured height, since its chip row only appears once a filter/sort is
// applied), and a pagination footer pinned to the bottom of the viewport.
// Built from the same designShell/APP_BAR_OFFSET primitives Admin uses, kept
// local here (rather than importing adminListScreenUi.js) so Approve's
// sub-screens don't reach across into the Administration module for what is
// really shared app-shell layout math.
export const approveStickyCustomFilterBar = {
  ...sharedStickyCustomFilterBarSx,
};

export const approveStickyFooterBar = {
  ...sharedStickyFooterBarSx,
};

export const approveTableHeadCell = {
  ...sharedTableHeadCellSx,
};

export const approveTableBodyCell = {
  ...sharedTableBodyCellSx,
};

// Matches the destructive action treatment used by GlobalConfirmDialog so
// bulk Reject actions clearly differ from neutral secondary buttons.
export const bulkRejectButtonSx = {
  backgroundColor: "#FEF2F2",
  borderColor: "#FECACA",
  color: "#DC2626",
  "&:hover": {
    backgroundColor: "#FEE2E2",
    borderColor: "#FCA5A5",
    color: "#DC2626",
  },
};

export const bulkSelectionCheckboxSx = {
  color: designColors.slate500,
  "&.Mui-checked, &.MuiCheckbox-indeterminate": {
    color: designColors.teal,
  },
  "&.Mui-disabled": {
    color: designColors.slate300,
  },
};

// Detail-panel text tokens, ported byte-for-byte from Administration's
// adminListScreenUi.js (UserDetailPanel's panelTitle/panelSectionTitle/
// panelMutedText/fieldLabel/fieldValue) — kept local for the same reason as
// the sticky-chrome tokens above rather than importing across modules.
export const approvePanelUi = {
  ...sharedPanelTextUi,
};

// Creation requires editor access; approver access alone does not show this button.
export const NewRequestButton = ({
  hasAccess,
  onClick,
  children = "New Request",
  disabled = false,
  sx,
}) => {
  if (hasAccess !== true) return null;

  return (
    <PrimaryButton
      startIcon={<AddIcon sx={{ fontSize: 18 }} />}
      sx={{ minWidth: HEADER_ACTION_BUTTON_WIDTH, ...sx }}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </PrimaryButton>
  );
};

// Disable-and-explain treatment
// for the Approve/Reject/Cancel buttons inside every request's detail
// panel — those already have their own varied button types (PrimaryButton,
// outlined Mui.Button, RequestCancelButton) and disabled conditions (e.g.
// combined with a loading state), so this wraps an already-built button
// element instead of rendering one of its own the way NewRequestButton
// does.
//
// Pass `hasAccess` for the ACCESS portion only — never fold a loading
// state into it — because the tooltip text is specifically about missing
// permission; showing "Access denied" while a request is simply mid-submit
// would be actively misleading. The button's own `disabled` prop still
// combines both (`disabled={!hasAccess || loading}`) as before.
//
// `wrapperSx` matters whenever the wrapped button doesn't auto-size to its
// own content — a bare inline `<span>` shrinks to fit, which silently
// breaks two different existing layouts if left unset:
//  - An equal-width row (e.g. `sx={{ flex: 1, minWidth: 0 }}` inside a
//    Mui.Stack direction="row") — pass the same flex/minWidth here to keep
//    the row intact.
//  - RequestCancelButton specifically, which always sets its own
//    width:"100%" expecting to fill whatever contains it — pass
//    `wrapperSx={{ width: "100%" }}` at that call site (not as a default
//    here: the OffDayDuty/OverTime edit-mode Approve button also uses this
//    component with no wrapperSx, inside a `justifyContent: "flex-end"`
//    row where it must stay auto-sized, not stretch — a blanket
//    width:"100%" default would have broken that pairing instead).
//
// Sizing the SPAN correctly (via wrapperSx) turned out not to be enough on
// its own — a grid/flex ITEM stretches to fill its own cell by default,
// but that only sizes the span itself, not what's inside it. The span is
// `display: "inline-flex"`, and a flex container's default alignment only
// stretches children along the CROSS axis (height, for a row-direction
// flex box) — nothing makes the child fill the MAIN axis (width) without
// being told to. So the button inside kept shrinking to its own text+
// padding width regardless of how correctly the span itself was sized,
// rendering narrower and left-aligned within its own cell instead of
// filling it — visibly narrower and misaligned next to an enabled
// sibling button once disabled. `"& > *"` forces whatever's actually
// passed as `children` (PrimaryButton, Mui.Button, RequestCancelButton — any
// of them) to fill the span's width, without needing to know or modify
// that child's own type.
//
// MUI's Tooltip needs the wrapping span to actually be in the DOM to
// attach hover listeners to in the first place (disabled elements don't
// dispatch pointer events, which is the whole reason this wrapper exists).
export const AccessGatedAction = ({
  hasAccess,
  deniedMessage,
  children,
  wrapperSx,
}) => {
  if (hasAccess) return children;

  return (
    <Tooltip title={deniedMessage} arrow slotProps={tooltipStyleProps}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          "& > *": { width: "100%" },
          ...wrapperSx,
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

// Returns a handler for the "Approval Center" breadcrumb crumb inside the
// detail side panels. Approve/index.js derives its selected screen from the
// URL query (?view=...), so pushing the bare pathname drops back to the
// Approval Center landing page.
export const useGoToApprovalCenter = () => {
  const history = useHistory();
  const location = useLocation();
  return () => history.push({ pathname: location.pathname, search: "" });
};

// Below this width the Approve list tables (min-width 920px, 8+ columns)
// would only scroll sideways, so each row is re-flowed into a stacked card
// instead: header row hidden, every cell becomes a "label: value" line whose
// label comes from the cell's `data-label` attribute (set from the column
// header name). It is pure CSS on the real <table>, so bulk-select
// checkboxes, row click -> detail panel, the expand chevron, the status
// timeline row and the empty state all keep working unchanged.
export const APPROVE_CARD_BREAKPOINT = 899.95;

export const approveCardTableSx = {
  [`@media (max-width:${APPROVE_CARD_BREAKPOINT}px)`]: {
    minWidth: 0,
    width: "100%",
    tableLayout: "auto",
    display: "block",
    "&.MuiTable-root > thead": { display: "none" },
    "&.MuiTable-root > tbody, &.MuiTable-root > tfoot": { display: "block", p: 1.25 },
    "&.MuiTable-root > tfoot": { pt: 0 },
    "&.MuiTable-root > tbody > tr, &.MuiTable-root > tfoot > tr": {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      backgroundColor: designColors.white,
      border: `1px solid ${designColors.slate200}`,
      borderRadius: "12px",
      p: 1.5,
      mb: 1.25,
    },
    "&.MuiTable-root > tbody > tr:has(> td.MuiTableCell-root[colspan]), &.MuiTable-root > tfoot > tr:has(> td.MuiTableCell-root[colspan])": {
      mt: -0.75,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
    "&.MuiTable-root > tbody > tr > td.MuiTableCell-root, &.MuiTable-root > tfoot > tr > td.MuiTableCell-root": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1.5,
      flex: "0 0 100%",
      minWidth: 0,
      boxSizing: "border-box",
      border: 0,
      py: 0.6,
      px: 0,
      textAlign: "right",
      whiteSpace: "normal",
      overflow: "visible",
      // Grid-style tables (frozen name columns, fixed pixel widths) must not
      // carry those into a card: no sticky offsets, no per-column widths.
      // Some row styles pin cells to a fixed height (e.g. 10px); a card cell
      // must size to its content or label and value overlap.
      height: "auto",
      minHeight: 0,
      position: "static",
      left: "auto",
      right: "auto",
      zIndex: "auto",
      width: "auto",
      maxWidth: "none",
      boxShadow: "none",
    },
    "&.MuiTable-root > tbody > tr > td.MuiTableCell-root[data-label]::before, &.MuiTable-root > tfoot > tr > td.MuiTableCell-root[data-label]::before": {
      content: "attr(data-label)",
      flex: "0 0 36%",
      maxWidth: "36%",
      textAlign: "left",
      fontSize: 12,
      fontWeight: 600,
      color: designColors.slate500,
      whiteSpace: "normal",
    },
    // Cards have room to wrap, so long names/values wrap instead of "Ashok Ku…"
    // (avatars keep their own clipping so they stay round).
    "&.MuiTable-root > tbody > tr > td.MuiTableCell-root[data-label] :not(.MuiAvatar-root):not(.MuiAvatar-root *):not(.MuiChip-root):not(.MuiChip-root *), &.MuiTable-root > tfoot > tr > td.MuiTableCell-root[data-label] :not(.MuiAvatar-root):not(.MuiAvatar-root *):not(.MuiChip-root):not(.MuiChip-root *)": {
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip",
      overflowWrap: "anywhere",
    },
    // Chips/pills keep their natural size (right-aligned by the cell's
    // space-between) instead of stretching across the whole card.
    "&.MuiTable-root > tbody > tr > td.MuiTableCell-root[data-label] > *:not(.MuiChip-root):not(.MuiButtonBase-root), &.MuiTable-root > tfoot > tr > td.MuiTableCell-root[data-label] > *:not(.MuiChip-root):not(.MuiButtonBase-root)": {
      flex: "1 1 0",
      minWidth: 0,
      width: "auto",
      justifyContent: "flex-end",
    },
    // An explicit empty data-label marks a card title cell: no label, left
    // aligned (e.g. a role/group name with its icon).
    "&.MuiTable-root > tbody > tr > td.MuiTableCell-root[data-label='']": {
      justifyContent: "flex-start",
      textAlign: "left",
    },
    "&.MuiTable-root > tbody > tr > td.MuiTableCell-root[data-label='']::before": {
      display: "none",
    },
    "&.MuiTable-root > tbody > tr > td.MuiTableCell-root[data-label=''] > *:not(.MuiChip-root):not(.MuiButtonBase-root)": {
      justifyContent: "flex-start",
    },
    // Checkbox + expand chevron share the card's first line.
    "&.MuiTable-root > tbody > tr > td.MuiTableCell-root.MuiTableCell-paddingCheckbox": {
      flex: "0 0 auto",
      order: -2,
      width: "auto",
    },
    "&.MuiTable-root > tbody > tr > td.MuiTableCell-root:not([data-label]):not([colspan]):not(.MuiTableCell-paddingCheckbox)": {
      flex: "0 0 auto",
      order: -1,
      marginLeft: "auto",
      width: "auto",
      py: 0,
      mb: -0.5,
    },
    "&.MuiTable-root > tbody > tr > td.MuiTableCell-root[colspan], &.MuiTable-root > tfoot > tr > td.MuiTableCell-root[colspan]": { display: "block", textAlign: "left" },
  },
};

// Labels body/footer cells from the column headers by position, so any table
// with a <thead> works without per-cell `data-label` wiring. Cells that
// already carry an explicit data-label are left alone. Skipped for rows with
// a colSpan cell (empty state, expanded timeline).
const labelTableCells = (table) => {
  const headRows = Array.from(table.tHead?.rows || []);
  if (!headRows.length) return;
  // Build a per-column label from every header row (handles rowSpan/colSpan
  // group headers such as "Shift Time" over Start / End).
  const grid = [];
  headRows.forEach((row, r) => {
    grid[r] = grid[r] || [];
    let col = 0;
    Array.from(row.cells).forEach((cell) => {
      while (grid[r][col] !== undefined) col += 1;
      const text = (cell.textContent || "").trim();
      const rs = cell.rowSpan || 1;
      const cs = cell.colSpan || 1;
      for (let i = 0; i < rs; i += 1) {
        grid[r + i] = grid[r + i] || [];
        for (let j = 0; j < cs; j += 1) grid[r + i][col + j] = text;
      }
      col += cs;
    });
  });
  const labels = [];
  grid.forEach((rowTexts) => {
    rowTexts.forEach((text, col) => {
      if (!text) return;
      const parts = labels[col] || [];
      if (parts[parts.length - 1] !== text) parts.push(text);
      labels[col] = parts;
    });
  });
  const rows = table.querySelectorAll(":scope > tbody > tr, :scope > tfoot > tr");
  rows.forEach((row) => {
    const cells = Array.from(row.cells);
    if (cells.some((cell) => cell.colSpan > 1)) return;
    cells.forEach((cell, index) => {
      const explicit = cell.hasAttribute("data-label") && !cell.hasAttribute("data-auto-label");
      if (explicit) return;
      const text = labels[index]?.join(" · ");
      if (text) {
        if (cell.getAttribute("data-label") !== text) cell.setAttribute("data-label", text);
        cell.setAttribute("data-auto-label", "1");
      } else if (cell.hasAttribute("data-auto-label")) {
        cell.removeAttribute("data-label");
        cell.removeAttribute("data-auto-label");
      }
    });
  });
};

export const ApproveTable = React.forwardRef(({ sx, ...props }, forwardedRef) => {
  const localRef = React.useRef(null);
  const setRef = React.useCallback(
    (node) => {
      localRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  React.useLayoutEffect(() => {
    const table = localRef.current;
    if (!table) return undefined;
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        labelTableCells(table);
      });
    };
    labelTableCells(table);
    const observer = new MutationObserver(schedule);
    observer.observe(table, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <MuiTable
      ref={setRef}
      {...props}
      sx={[...(Array.isArray(sx) ? sx : [sx]), approveCardTableSx]}
    />
  );
});
ApproveTable.displayName = "ApproveTable";
